/**
 * @typedef {object} MediaStartedPlayingEventDetail
 * @property {Element} resource The element that started playing.
 */
/**
 * @typedef {object} SlideshowSelectEventDetail
 * @property {number} index Index of the newly selected slide.
 * @property {number} previousIndex Index of the previously selected slide.
 * @property {boolean} userInitiated Whether the change came from a user gesture.
 * @property {string} [trigger] Optional trigger source (e.g. `select`, `scroll`, `drag`).
 * @property {Element} [slide] The newly selected slide element, when available.
 * @property {string | null} [id] The slide id attribute, when available.
 */
/**
 * @typedef {object} QuantitySelectorUpdateDetail
 * @property {string} variantId Variant whose quantity changed.
 * @property {number} quantity New quantity value.
 */
export class ThemeEvents {
  static mediaStartedPlaying = 'media:started-playing';
  static quantitySelectorUpdate = 'quantity-selector:update';
}
/**
 * Custom event signalling that a media resource has started playback.
 *
 * @fires ThemeEvents#mediaStartedPlaying
 * @param {Element} resource The element that started playing.
 */ export class MediaStartedPlayingEvent extends Event {
  /**
   * @param {Element} resource The element that started playing.
   */
  constructor(/** @type {Element} */ resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    /** @type {MediaStartedPlayingEventDetail} */ this.detail = {
      resource: resource,
    };
  }
}
/**
 * Custom event signalling that a slide has been selected in a slideshow.
 *
 * @fires SlideshowSelectEvent#eventName
 * @param {SlideshowSelectEventDetail} data Detail describing the selection.
 */ export class SlideshowSelectEvent extends Event {
  /**
   * @param {SlideshowSelectEventDetail} data Detail describing the selection.
   */
  constructor(/** @type {SlideshowSelectEventDetail} */ data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }
  /** @type {SlideshowSelectEventDetail} */ detail;
  static eventName = 'slideshow:select';
}
